/**
 * Lo apartado, on screen (the owner, 2026-10-08: «we need stock reservation by both orders and the
 * till»): what each product has left to sell or to promise once the pending orders have what they
 * set aside, what an order being taken or a sale at the till would take beyond that, and which
 * orders the stock no longer covers. The sums are the core's (`@abastece/core` `apartados.ts`), the
 * same the books refuse an order by; this reads them off what the phone holds. Pure.
 */

import { ZERO_QTY, addQty, fromUnits, saleToBase, subQty, type Milli } from '@abastece/shared';
import {
  DONDE_SE_APARTA,
  apartadoPorProducto,
  cobertura,
  libre,
  loQuePide,
  primeroElPrimero,
  type Existencia,
  type Faltante,
  type FactorDe,
  type Pedido,
  type UnidadVenta,
} from '@abastece/core';

/** Each presentation's factor, by its id. */
export function factoresDe(unidades: readonly Pick<UnidadVenta, 'id' | 'factor'>[]): FactorDe {
  const m = new Map(unidades.map((u) => [u.id, u.factor]));
  return (id) => m.get(id);
}

/** What the shop's floor holds of each product, where orders set stock aside. */
export function hayEnLaTienda(existencias: readonly Pick<Existencia, 'productoId' | 'ubicacion' | 'cantidad'>[]): (productoId: string) => Milli {
  const m = new Map<string, Milli>();
  for (const e of existencias) if (e.ubicacion === DONDE_SE_APARTA) m.set(e.productoId, e.cantidad);
  return (id) => m.get(id) ?? ZERO_QTY;
}

/** A product somebody wants more of than is free: how much they want, and how much is free. */
export interface NoAlcanza {
  productoId: string;
  pide: Milli;
  libre: Milli;
}

/** What each line of something being rung up takes, by product, in base units. */
function porProducto(lineas: readonly { productoId: string | null; cantidad: Milli; factor: Milli | null | undefined }[]): Map<string, Milli> {
  const m = new Map<string, Milli>();
  for (const l of lineas) {
    if (!l.productoId) continue;
    m.set(l.productoId, addQty(m.get(l.productoId) ?? ZERO_QTY, saleToBase(l.cantidad, l.factor ?? fromUnits(1))));
  }
  return m;
}

/**
 * An order being taken: each product it asks more of than is free, after the pending orders. What
 * the books would refuse it for, said before it is sent (`pedido.tomado`).
 *
 * An order being changed (`cambiado`, as it stood) sets nothing aside against itself, and only what
 * it asks more of than it held is held to what is free (`pedido.actualizado`).
 */
export function noAlcanzaParaElPedido(
  lineas: readonly { productoId: string; cantidad: Milli; factor: Milli }[],
  datos: { pedidos: readonly Pedido[]; factorDe: FactorDe; hayDe: (productoId: string) => Milli; cambiado?: Pedido | undefined },
): NoAlcanza[] {
  const apartado = apartadoPorProducto(datos.pedidos, datos.factorDe, datos.cambiado?.id ?? null);
  const tenia = datos.cambiado ? loQuePide(datos.cambiado.lineas, datos.factorDe) : new Map<string, Milli>();
  const faltan: NoAlcanza[] = [];
  for (const [productoId, pide] of porProducto(lineas)) {
    if (pide <= (tenia.get(productoId) ?? ZERO_QTY)) continue;
    const queda = libre(datos.hayDe(productoId), apartado.get(productoId) ?? ZERO_QTY);
    if (pide > queda) faltan.push({ productoId, pide, libre: queda });
  }
  return faltan;
}

/**
 * Whether an order being changed asks for more of some product than it held: only then can it take
 * what another order is waiting for, and need the server's word before it is saved.
 */
export function pideMasQueAntes(
  lineas: readonly { productoId: string; cantidad: Milli; factor: Milli }[],
  antes: Pick<Pedido, 'lineas'>,
  factorDe: FactorDe,
): boolean {
  const tenia = loQuePide(antes.lineas, factorDe);
  for (const [productoId, pide] of porProducto(lineas)) if (pide > (tenia.get(productoId) ?? ZERO_QTY)) return true;
  return false;
}

/** A product a sale would take from what orders set aside: how much of it, and the orders it leaves short. */
export interface TomaApartado {
  productoId: string;
  /** What the sale takes past what is free, up to what the orders hold: what they would come up short. */
  toma: Milli;
  /** The orders that would get less of it, the first taken first: the last taken, as they are served last. */
  pedidos: Pedido[];
}

/**
 * A sale at the till: each product it takes of what other orders set aside, and which orders it
 * leaves short. The order being charged, if it is one, sets nothing aside against itself; a product
 * no order holds is not this question, however little the shelf has. Only at the shop: a till on a
 * truck sells what the truck carries, which nothing sets aside.
 */
export function tomaDeLoApartado(
  lineas: readonly { productoId: string | null; cantidad: Milli; factor: Milli | null | undefined }[],
  datos: { pedidos: readonly Pedido[]; factorDe: FactorDe; hayDe: (productoId: string) => Milli; pedidoId: string | null; ubicacion: string },
): TomaApartado[] {
  if (datos.ubicacion !== DONDE_SE_APARTA) return [];
  const apartado = apartadoPorProducto(datos.pedidos, datos.factorDe, datos.pedidoId);
  const toma: TomaApartado[] = [];
  for (const [productoId, lleva] of porProducto(lineas)) {
    const deOtros = apartado.get(productoId) ?? ZERO_QTY;
    if (deOtros <= 0) continue;
    const queda = libre(datos.hayDe(productoId), deOtros);
    if (lleva <= queda) continue;
    const pasa = subQty(lleva, queda);
    const hay = datos.hayDe(productoId);
    const conEl = datos.pedidos
      .filter((p) => p.estado === 'pendiente' && p.id !== datos.pedidoId && p.lineas.some((l) => l.productoId === productoId))
      .sort(primeroElPrimero);
    // Served in turn from what the shelf holds, before the sale and after it: who gets less.
    const faltas = (hayAhi: Milli) => {
      let resta = hayAhi > 0 ? hayAhi : ZERO_QTY;
      return conEl.map((p) => {
        const pide = loQuePide(p.lineas, datos.factorDe).get(productoId) ?? ZERO_QTY;
        const falta = pide > resta ? subQty(pide, resta) : ZERO_QTY;
        resta = pide > resta ? ZERO_QTY : subQty(resta, pide);
        return falta;
      });
    };
    const antes = faltas(hay);
    const despues = faltas(subQty(hay, lleva));
    const cortos = conEl.filter((_, i) => despues[i]! > antes[i]!);
    // Nothing on the shelf already, the orders get less of what comes in next: the last one first.
    toma.push({ productoId, toma: pasa < deOtros ? pasa : deOtros, pedidos: cortos.length > 0 ? cortos : conEl.slice(-1) });
  }
  return toma;
}

/** Whether the owner's yes covers what a sale takes now: every product, up to what they said yes to. */
export function cubreLoApartado(toma: readonly TomaApartado[], hasta: Readonly<Record<string, number>> | null): boolean {
  return toma.every((x) => hasta !== null && (hasta[x.productoId] ?? 0) >= x.toma);
}

/** A product a sale takes more of than is free, with `venderSoloLoQueHay` on. */
export interface FaltaEnVenta {
  productoId: string;
  lleva: Milli;
  libre: Milli;
  /** What pending orders hold of it, the order being charged aside. */
  apartado: Milli;
}

/**
 * The till with `venderSoloLoQueHay` on: each product the sale takes more of than is free, what the
 * shop holds less what pending orders set aside (`pedidos` empty when they set nothing aside), the
 * order being charged aside. What the books would refuse a sale asking `soloSiHay` for, said as
 * it is rung up. Only at the shop, where orders set stock aside.
 */
export function faltaParaLaVenta(
  lineas: readonly { productoId: string | null; cantidad: Milli; factor: Milli | null | undefined }[],
  datos: { pedidos: readonly Pedido[]; factorDe: FactorDe; hayDe: (productoId: string) => Milli; pedidoId: string | null; ubicacion: string },
): FaltaEnVenta[] {
  if (datos.ubicacion !== DONDE_SE_APARTA) return [];
  const apartado = apartadoPorProducto(datos.pedidos, datos.factorDe, datos.pedidoId);
  const faltan: FaltaEnVenta[] = [];
  for (const [productoId, lleva] of porProducto(lineas)) {
    const deOtros = apartado.get(productoId) ?? ZERO_QTY;
    const queda = libre(datos.hayDe(productoId), deOtros);
    if (lleva > queda) faltan.push({ productoId, lleva, libre: queda, apartado: deOtros });
  }
  return faltan;
}

/** Whether the owner's yes covers what a sale takes now past what is free: each product, up to how much. */
export function cubreLaFalta(faltan: readonly FaltaEnVenta[], hasta: Readonly<Record<string, number>> | null): boolean {
  return faltan.every((x) => hasta !== null && (hasta[x.productoId] ?? 0) >= x.lleva);
}

/** The pending orders the stock no longer covers, with what each lacks (`cobertura`). */
export function pedidosCortos(datos: { pedidos: readonly Pedido[]; factorDe: FactorDe; hayDe: (productoId: string) => Milli }): Map<string, Faltante[]> {
  return cobertura(datos.pedidos, datos.factorDe, datos.hayDe);
}

/** What the pending orders set aside of one product, for its page and the inventory list. */
export function apartadoDe(productoId: string, datos: { pedidos: readonly Pedido[]; factorDe: FactorDe }): Milli {
  return apartadoPorProducto(datos.pedidos, datos.factorDe).get(productoId) ?? ZERO_QTY;
}
